import { useCallback } from 'react';
import { pickBaselinePrompt } from '../data/baselinePrompts';
import {
  FREE_WRITE_DURATION_MS,
  useFreeWriteInput,
  type FreeWriteResult,
} from '../lib/useFreeWriteInput';
import type { BaselineSnapshot, KeyboardLayoutId } from '../types';
import { ALL_TAUGHT_KEYS } from '../data/curriculum';
import Keyboard from './Keyboard';

interface BaselineCompositionScreenProps {
  keyboardLayout: KeyboardLayoutId;
  promptSeed?: number;
  onFinish: (snapshot: BaselineSnapshot) => void;
  onCancel: () => void;
}

export default function BaselineCompositionScreen({
  keyboardLayout,
  promptSeed,
  onFinish,
  onCancel,
}: BaselineCompositionScreenProps) {
  const prompt = pickBaselinePrompt(promptSeed);

  const handleDone = useCallback(
    (r: FreeWriteResult) => {
      onFinish({
        kind: 'composition',
        wpm: r.wpm,
        accuracy: r.accuracy,
        correctChars: r.correctChars,
        totalKeystrokes: r.totalKeystrokes,
        backspaces: r.backspaces,
        durationMs: r.durationMs,
        takenAt: new Date().toISOString(),
        promptId: prompt.id,
      });
    },
    [onFinish, prompt.id],
  );

  const input = useFreeWriteInput(FREE_WRITE_DURATION_MS, handleDone);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <header>
        <p className="label">Udgangspunkt · hverdag</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">
          Skriv frit i ét minut
        </h1>
        <p className="mt-2 text-sm text-muted">{prompt.text}</p>
        <p className="mt-1 text-xs text-faint">
          Skriv dine egne ord — som når du tænker og skriver uden at kopiere fra
          skærmen.
        </p>
      </header>

      <div className="grid grid-cols-4 gap-3">
        <Stat label="Tid" value={formatMs(input.elapsedMs)} />
        <Stat label="WPM" value={input.liveWpm.toFixed(0)} />
        <Stat
          label="Flyd"
          value={`${(input.liveFluency * 100).toFixed(0)}%`}
        />
        <Stat label="Tegn" value={String(input.text.length)} />
      </div>

      <div
        className="min-h-[8rem] rounded-xl bg-zinc-50 p-4 text-lg leading-relaxed ring-1 ring-zinc-200 dark:bg-zinc-900/60 dark:ring-white/5"
        aria-live="polite"
      >
        {input.text.length === 0 ? (
          <span className="text-faint">Begynd at skrive…</span>
        ) : (
          <span className="whitespace-pre-wrap text-strong">{input.text}</span>
        )}
      </div>

      <Keyboard
        layoutId={keyboardLayout}
        unlockedKeys={ALL_TAUGHT_KEYS}
        hintLevel="none"
        showFingerDot={false}
      />

      <div className="flex justify-between gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Annuller
        </button>
        <p className="self-center text-xs text-faint">
          {formatMs(input.remainingMs)} tilbage · Escape afslutter
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="surface p-3 text-center">
      <p className="text-xs text-faint">{label}</p>
      <p className="mt-0.5 font-mono text-lg font-semibold text-strong">
        {value}
      </p>
    </div>
  );
}

function formatMs(ms: number): string {
  const s = Math.ceil(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}:${String(r).padStart(2, '0')}` : `${r}s`;
}
